<script lang="ts">
  import { onMount } from "svelte"
  import ImageCropper from "$app/components/ImageCropper.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Navigation from "./Navigation.svelte"
  import addPhoto from "@images/onboarding/add-a-photo.png"
  import img from "@images/onboarding/plan-setup.jpg"
  import { toast, xhr } from "$app/utils"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"
  import { monthNames } from "$app/constants"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"

  let props = $props()
  type TzType = { name: string }
  let time_zones: TzType[] = props.time_zones

  onboardingStore.completed = true

  let changedLogo = $state(false)
  let openImgCropper = $state(false)
  let logo: any = $state(null),
    fileImage: any = $state(null),
    logoTmp: any = $state(null),
    fileInput: HTMLInputElement = $state(null)

  let form = {
    name: appStore.plan.name,
    company_name: appStore.plan.company_name,
    zipcode: appStore.plan.zipcode,
    market_id: appStore.plan.market_id,
    nonprofit: appStore.plan.nonprofit,
    fiscal_year_start_month: appStore.plan.fiscal_year_start_month,
    time_zone: appStore.plan.time_zone
  }
  if (form.name === "New Plan") {
    form.name = ""
  }

  let errors: any = $state({})

  async function nextStep(): Promise<void> {
    onboardingStore.saving = true
    errors = {}
    const formData = new FormData()

    if (!form.company_name || form.company_name.length <= 0) {
      errors.company_name = "Please enter your company name"
      onboardingStore.saving = false
      return
    }
    formData.append("plan[name]", form.company_name)
    formData.append("plan[company_name]", form.company_name)
    formData.append("plan[time_zone]", form.time_zone)
    formData.append(
      "plan[fiscal_year_start_month]",
      form.fiscal_year_start_month
    )
    formData.append(
      "plan[onboarding_step]",
      onboardingStore.current_onboarding_step
    )

    if (changedLogo) {
      formData.append("plan[logo]", fileImage)
    }

    try {
      const resp = await xhr.patch(
        `/plan_onboarding/${appStore.plan.id}.json?`,
        formData
      )

      appStore.plan = { ...appStore.plan, ...resp.data.plan }
    } catch (e) {
      console.error(e)
      if (e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      onboardingStore.current_onboarding_step = onboardingStore.nextStepName
      onboardingStore.saving = false
    }
  }

  function onFileSelected(e: any): void {
    fileImage = e.target.files[0]
    let reader = new FileReader()
    reader.readAsDataURL(fileImage)
    reader.onload = (e) => {
      changedLogo = true
      logoTmp = e.target.result
      openImgCropper = true
    }
  }

  onMount(() => {
    if (appStore.plan.has_logo) {
      logo = appStore.plan.logo_url
      const name = logo.split("/").pop().split("#")[0].split("?")[0]
      if (name.match(/\.(jpg|jpeg)$/i)) {
        fileImage = { type: "image/jpeg", name }
      } else {
        fileImage = { type: "image/png", name }
      }
    } else {
      logo = addPhoto
    }

    onboardingStore.setBrowserHistory({}, "plan_setup")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full lg:w-1/2 xl:pr-16">
    <Navigation step="plan_setup" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      Set Up Your Plan
    </h2>

    <div class="space-y-8 mt-6 md:mt-8 max-w-xl">
      <div>
        <div class="flex justify-start items-center">
          <button
            class="h-24 w-24 rounded-full bg-lwp-pale-blue hover:border-blue-500 hover:cursor-pointer flex justify-center items-center border-4 border-white overflow-hidden"
            onclick={() => {
              fileInput.click()
            }}
          >
            {#if appStore.plan.has_logo}
              <img
                data-name="plan-logo"
                src={logo}
                class="w-24 h-24 rounded-full bg-white"
                alt="Logo"
              />
            {:else}
              <img
                data-name="plan-logo"
                src={logo}
                class="w-16 h-16 bg-lwp-pale-blue"
                alt="Logo"
              />
            {/if}
          </button>
          <div class="ml-4 justify-center">
            <button
              onclick={() => {
                fileInput.click()
              }}
              class="relative cursor-pointer text-lg font-medium text-white hover:text-blue-400 hover:underline focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-600"
            >
              {#if appStore.plan.has_logo}
                <span>Change your company logo</span>
              {:else}
                <span>Upload your company logo</span>
              {/if}
            </button>
          </div>
          {#if changedLogo}
            <ImageCropper
              image={logoTmp}
              open={openImgCropper}
              file={fileImage}
              cropShape="round"
              cropperClose={() => {
                openImgCropper = false
              }}
              imageCropped={(data) => {
                logo = data.image
                fileImage = data.file
                appStore.plan.has_logo = true
              }}
            />
          {/if}
          <input
            type="file"
            class="hidden"
            bind:this={fileInput}
            onchange={onFileSelected}
            accept=".jpg, .jpeg, .png"
          />
        </div>

        <div class="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-y-4 gap-x-4">
          <div class="lg:col-span-2 max-w-lg">
            <label for="plan[company_name]" class="text-white text-lg">
              Company Name
            </label>
            <div class="mt-2 input-cont" class:error={errors.company_name}>
              <input
                type="text"
                bind:value={form.company_name}
                data-name="company_name"
                class="input-lg input"
              />
              <div class="msg">{errors.company_name}</div>
            </div>
          </div>
          <div class="lg:col-span-2 max-w-lg">
            <label for="plan[time_zone]" class="text-white text-lg">
              Time Zone
              <span class="text-xs">
                (Used for email notification and reports)
              </span>
            </label>
            <div class="mt-2 input-cont" class:error={errors.time_zone}>
              <select
                class="input input-lg max-w-xs"
                bind:value={form.time_zone}
                data-name="time_zone"
              >
                {#each time_zones as tz (tz.name)}
                  <option value={tz.name}>{tz.name}</option>
                {/each}
              </select>
              <div class="msg">{errors.time_zone}</div>
            </div>
          </div>

          <div class="lg:col-span-2 max-w-lg">
            <label
              for="plan[fiscal_year_start_month]"
              class="text-white text-lg"
            >
              Fiscal Year Start Month
              <span class="text-xs"> (The month your fiscal Q1 begins)</span>
            </label>
            <select
              name="plan[fiscal_year_start_month]"
              class="input-lg max-w-xs"
              bind:value={form.fiscal_year_start_month}
            >
              {#each monthNames as month, i}
                <option value={i + 1}>{month}</option>
              {/each}
            </select>
          </div>
        </div>

        <PrevNextSkipButtons nextStepFn={nextStep} showSkip={false} />
      </div>
    </div>
  </div>
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-0 lg:-right-0 md:w-1/3 lg:w-1/2 lg:h-full bg-lwp-light-grey bg-center bg-no-repeat bg-cover"
  style="background-image: url({img})"
></div>
